import _extends from 'babel-runtime/helpers/extends';
import PropTypes from '../_util/vue-types';
import SelectPropTypes from './SelectPropTypes';
import buttonTypes from '../button/buttonTypes';
import { SHOW_ALL, SHOW_PARENT, SHOW_CHILD } from './strategies';
var ButtonType = buttonTypes().type;

var CascaderOptionType = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.any,
  disabled: PropTypes.bool,
  children: PropTypes.array,
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}).loose;

var FieldNamesType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.string
}).def({
  children: 'children',
  label: 'label',
  value: 'value'
}).loose;

var CascaderProps = _extends({}, SelectPropTypes, {
  /** 可选项数据源 */
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.arrayOf(CascaderOptionType)]).def([]),
  /** 默认的选中项 */
  defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  /** 指定选中项 */
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  /** 选择后展示的渲染函数 */
  displayRender: PropTypes.func,
  transitionName: PropTypes.string.def('slide-up'),
  choiceTransitionName: PropTypes.string.def('zoom'),
  popupStyle: PropTypes.object.def(function () {
    return {};
  }),
  popupAlign: PropTypes.object.def(function () {
    return {};
  }),
  /** 自定义浮层类名 */
  popupClassName: PropTypes.string.def(''),
  /** 浮层预设位置：`bottomLeft` `bottomRight` `topLeft` `topRight` */
  popupPlacement: PropTypes.oneOf(['bottomLeft', 'bottomRight', 'bottom', 'topLeft', 'topRight', 'top']).def('bottomLeft'),
  builtinPlacements: PropTypes.object,
  /** 输入框占位文本*/
  placeholder: PropTypes.string,
  /** 输入框大小，可选 `large` `default` `small` */
  size: PropTypes.oneOf(['large', 'default', 'small']),
  /** 输入框形状 */
  shape: PropTypes.oneOf(['default', 'round']),
  /** 禁用*/
  disabled: PropTypes.bool,
  /** 是否支持清除*/
  allowClear: PropTypes.bool,
  /** 显示下拉按钮 */
  showArrow: PropTypes.bool.def(true),
  /** 显示确认按钮 */
  showConfirm: PropTypes.bool,
  /** 显示关闭按钮 */
  showClose: PropTypes.bool.def(true),
  /** 直接显示弹出内容 */
  isModal: PropTypes.bool,
  showCheckAll: PropTypes.bool,
  showSelection: PropTypes.bool.def(true),
  showSearch: PropTypes.oneOfType([Boolean]),
  searchPlaceholder: PropTypes.string, // [Legacy] Confuse with placeholder
  searchValue: PropTypes.string,
  /** 输入选择框属性 */
  inputProps: PropTypes.object,
  popupMatchSelectWidth: PropTypes.bool,
  notFoundContent: PropTypes.any,
  // 自定义搜索函数
  filterTreeNode: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /** 显示选择策略 */
  showCheckedStrategy: PropTypes.oneOf([SHOW_ALL, SHOW_PARENT, SHOW_CHILD]).def(SHOW_CHILD),
  // 自定义过滤函数
  filterFn: PropTypes.func,
  // 默认过滤初始值
  defaultFilterValue: PropTypes.any,
  /** 设置`uniqueKey`可以防止唯一ID有重复值时，返回ID路径 */
  uniqueKey: PropTypes.bool,
  /** 当设置`unrelated`为true时，树形数组里的节点讲没有联动关系 */
  unrelated: PropTypes.bool,
  prefixCls: PropTypes.string,
  selectPrefixCls: PropTypes.string,
  getPopupContainer: PropTypes.func,
  fieldNames: FieldNamesType,
  autoFocus: PropTypes.bool,
  suffixIcon: PropTypes.any,
  inputIcon: PropTypes.any,
  clearIcon: PropTypes.any,
  removeIcon: PropTypes.any,
  closeIcon: PropTypes.any,
  expandIcon: PropTypes.any,
  searchIcon: PropTypes.any,
  // 是否多选
  multiple: PropTypes.oneOf([false, true]),
  // 面板标题，默认不展示
  title: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  // 当前激活的标签
  defaultActiveKey: PropTypes.number.def(0),
  // 顶部标签切换页面
  tabs: PropTypes.array,
  /**
   * 当有tabs时，切换tab有两种情况，
   * 第一种是tab之间的节点有重复(如车型选择[细分市场|品牌])，
   * 第二种是没有重复(如型号选择[车型1|车型2])
   * 当`tabGroup`设置为'dimension'时，允许节点重复
   */
  tabGroup: PropTypes.string.def('default'),
  // 列宽，数字表示每列的宽度，数组表示每列单独宽度
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  // 内容区的高度
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).def(180),
  // 列名
  columnNames: PropTypes.arrayOf(PropTypes.string),
  // 已选弹出框列名
  popupColumnNames: PropTypes.array,
  // 自定义label
  labelRender: PropTypes.func,
  // 第一层显示分组
  showGroup: PropTypes.bool,
  // 显示过滤层
  showFilter: PropTypes.bool.def(true),
  // 扩张过滤层
  expandFilter: PropTypes.bool.def(true),
  // 快速定位
  fastFinder: PropTypes.oneOf(['letter', 'dropdown', false]).def('letter'),
  // 快速定位位置
  fastFinderPos: PropTypes.oneOf(['left', 'top']).def('left'),
  // 最大显示子节点数，超过会滚动加载
  maxTreeNodes: PropTypes.number.def(499),
  // 当数据量超过阈值时，可显示分组最大索引值
  visibleEndIndex: PropTypes.number.def(2),
  // 每列叶子节点个数，0表示随着长度自动换行
  leafsPerColumn: PropTypes.number.def(1),
  // 最小值
  min: PropTypes.number,
  // 最大值
  max: PropTypes.number,
  // 加载中
  loading: PropTypes.bool.def(false),
  // 校验函数
  validatorFn: PropTypes.func,
  /** 确认按钮文字*/
  okText: PropTypes.any,
  /** 确认按钮类型*/
  okType: ButtonType,
  /** 确认按钮属性*/
  okButtonProps: PropTypes.object,
  /** 取消按钮文字*/
  cancelText: PropTypes.any,
  /** 取消按钮属性*/
  cancelButtonProps: PropTypes.object,
  /** 重置按钮文字*/
  resetText: PropTypes.any,
  /** 重置按钮属性*/
  resetButtonProps: PropTypes.object,
  /** 重置值 */
  resetValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  /** 追加按钮 */
  extraButton: PropTypes.any
});
export default CascaderProps;